import React, { createContext, useState, useEffect, useContext } from 'react';
import { ru } from '../locales/ru';
import { en } from '../locales/en';
import { zh } from '../locales/zh';
import { uk } from '../locales/uk';
import { getCookie, setCookie } from '../utils/cookies';

const translations = { ru, en, zh, uk };

const detectBrowserLanguage = () => {
  const raw = (navigator.language || navigator.userLanguage || '').toLowerCase();
  if (raw.startsWith('zh')) return 'zh';
  if (raw.startsWith('uk') || raw.startsWith('ua')) return 'uk';
  if (raw.startsWith('ru') || raw.startsWith('be') || raw.startsWith('kk') || raw.startsWith('ky')) return 'ru';
  return 'en';
};

const getInitialLanguage = () => {
  const saved = getCookie('tg_prime_lang');
  if (saved && translations[saved]) return saved;
  const browser = detectBrowserLanguage();
  if (browser && translations[browser]) return browser;
  return 'ru';
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = (langCode) => {
    if (translations[langCode]) {
      setLanguageState(langCode);
      setCookie('tg_prime_lang', langCode, 365);
    }
  };

  useEffect(() => {
    const savedLang = getCookie('tg_prime_lang');
    if (savedLang) return;

    const browserLang = detectBrowserLanguage();
    if (browserLang && translations[browserLang]) return;

    fetch('https://ipapi.co/json/')
      .then((res) => {
        if (!res.ok) throw new Error('Network response error');
        return res.json();
      })
      .then((data) => {
        const country = data.country_code;
        let detected = 'en';

        if (country === 'CN') {
          detected = 'zh';
        } else if (country === 'UA') {
          detected = 'uk';
        } else if (['RU', 'BY', 'KZ', 'KG', 'MD'].includes(country)) {
          detected = 'ru';
        }

        setLanguage(detected);
      })
      .catch((err) => {
        console.warn('IP Geolocation lookup failed, defaulting to Russian.', err);
        setLanguage('ru');
      });
  }, []);

  const t = (path, values = {}) => {
    const keys = path.split('.');
    let value = translations[language];
    for (const key of keys) {
      if (value && value[key] !== undefined) {
        value = value[key];
      } else {
        return path;
      }
    }
    if (typeof value === 'string' && values && Object.keys(values).length > 0) {
      return value.replace(/\{(\w+)\}/g, (_, k) =>
        values[k] !== undefined ? values[k] : `{${k}}`
      );
    }
    return value;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
